import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import SelectedEmiBankForm from "./selectedEmiBankForm";
import { removeSelectedIntruments } from "../../../../slices/SavedInstrumentSlice";
import { changeBankDropdown, creditBankSelected } from "../../../../slices/EmiSlice";
import { methodSelectedEvent, emiDropdownSelectedEvent, emiDropdownEnteredEvent } from "../../../../Utils/uiAnalytics";
import EmiDisplayCard from "./emiDisplayCard";

export default function CreditCardEmi() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const emiCreditBankSelected = useSelector((state) => creditBankSelected(state));
  const [showEmiPlanDetails, setShowEmiPlanDetails] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [selectedEmiBank, setSelectedEmiBank] = useState("");
  const [selectedEmiBankTitle, setSelectedEmiBankTitle] = useState("");
  const emiPaymentDetails = useSelector(state => state.emiCardDetails.emiPaymentDetails)

  const paymentMethodDictionary = useSelector(state => state.paymentSessionDetails?.paymentMethodDictionary)
  const emiPaymentMethod = emiPaymentDetails?.Emi ? emiPaymentDetails.Emi : paymentMethodDictionary?.Emi || []

  const emiOptions = emiPaymentMethod
    ?.filter(
      (method) => method.type === "Emi" && method.brand === "CreditCardEMI"
    )
    ?.reduce((uniqueOptions, method) => {
      const brand = method?.brand;
      const issuer = method?.emiMethod?.issuer;
      const issuerTitle = method?.emiMethod?.issuerTitle;
      const logoUrl = method?.logoUrl;
      const emiAmount = method?.emiMethod?.emiAmount;
      const emiAmountLocaleFull = method?.emiMethod?.emiAmountLocaleFull;
      const effectiveInterestRate = method?.emiMethod?.effectiveInterestRate;
      const emiMethod = method?.emiMethod;
      const discountType = method?.emiMethod?.applicableOffer?.discount?.type;
      let emiOfferTypes = discountType ? [discountType] : [];
      if (effectiveInterestRate === 0) {
        emiOfferTypes.push("NoCostEmi");
      }
      let existingOptionIndex = uniqueOptions.findIndex((option) => option.value === issuer);

      let emiData = {
        brand: brand,
        value: issuer,
        name: issuerTitle,
        logoUrl: logoUrl,
        emiAmount: emiAmount,
        emiAmountLocaleFull: emiAmountLocaleFull,
        emiOfferTypes: emiOfferTypes,
        emiMethod: emiMethod,
      }

      if (existingOptionIndex === -1) {
        uniqueOptions.push(emiData);
      } else {
        let existingOption = uniqueOptions[existingOptionIndex];
        if (existingOption?.emiMethod?.interestRate > emiData?.emiMethod?.effectiveInterestRate
          && emiData?.emiMethod?.effectiveInterestRate !== 0) {
          uniqueOptions[existingOptionIndex] = emiData;
        }
        emiOfferTypes.forEach((type) => {
          if (!existingOption.emiOfferTypes.includes(type)) {
            existingOption.emiOfferTypes.push(type);
          }
        });
      }
      return uniqueOptions;
    }, []);

  const filterOption = (input, option) => (option?.props?.children ?? "").toLowerCase().includes(input.toLowerCase());

  const handleSelectedBank = (item) => {
    setShowCardForm(false);
    dispatch(changeBankDropdown(item.value));
    setSelectedEmiBank(item);
    setShowEmiPlanDetails(true);
    methodSelectedEvent("Emi", item?.brand);
  };

  useEffect(() => {
    let name = sortedEmiOptions.find(d => d.value === emiCreditBankSelected)?.name;
    setSelectedEmiBankTitle(name)
  }, [emiCreditBankSelected])

  const handleEmiBank = (value) => {
    dispatch(changeBankDropdown(value));
    dispatch(removeSelectedIntruments());
    emiDropdownEnteredEvent();
    const selectedBankDetails = emiPaymentMethod
      ?.filter(
        (method) =>
          method.type === "Emi" &&
          method.brand === "CreditCardEMI" &&
          method.emiMethod.issuer === value
      )
      ?.map((method) => ({
        brand: method.brand,
        value: method.emiMethod.issuer,
        name: method.emiMethod.issuerTitle,
        logoUrl: method.logoUrl,
        emiAmount: method.emiMethod.emiAmount,
        emiAmountLocaleFull: method.emiMethod.emiAmountLocaleFull,
      }));
    if (selectedBankDetails.length > 0) {
      setSelectedEmiBank(selectedBankDetails[0]);
    }
    setShowEmiPlanDetails(true);
    methodSelectedEvent("Emi", "CreditCardEMI");
  };

  const sortedEmiOptions = emiOptions?.sort((a, b) =>
    a?.name?.localeCompare(b?.name)
  );

  const emiOptionsBasedOnAmount = [...emiOptions].sort(
    (a, b) => a.emiMethod.effectiveInterestRate - b.emiMethod.effectiveInterestRate
  );

  const sortedEmiOptionsBasedOnEmiOffer = emiOptionsBasedOnAmount.sort((a, b) => {
    const aHasNoCost = Array.isArray(a.emiOfferTypes) && a.emiOfferTypes.includes('NoCostEmi');
    const bHasNoCost = Array.isArray(b.emiOfferTypes) && b.emiOfferTypes.includes('NoCostEmi');
    const aHasEmi = Array.isArray(a.emiOfferTypes) && a.emiOfferTypes.length > 0;
    const bHasEmi = Array.isArray(b.emiOfferTypes) && b.emiOfferTypes.length > 0;

    if (aHasNoCost && !bHasNoCost) return -1;
    if (!aHasNoCost && bHasNoCost) return 1;

    if (aHasEmi && !bHasEmi) return -1;
    if (!aHasEmi && bHasEmi) return 1;

    return 0;
  });

  useEffect(() => {
    if (emiCreditBankSelected === "All Banks") {
      setShowEmiPlanDetails(false);
    }
  }, [emiCreditBankSelected]);

  return (
    <div className="bankSelect">
      <Select
        style={{ marginBottom: "12px" }}
        showSearch
        placeholder={t("ALL_BANKS")}
        optionFilterProp="name"
        onChange={handleEmiBank}
        onFocus={() => emiDropdownSelectedEvent()}
        filterOption={filterOption}
        value={emiCreditBankSelected}
      >
        {sortedEmiOptions?.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.name}
          </Select.Option>
        ))}
      </Select>
      {showEmiPlanDetails ? (
        <SelectedEmiBankForm
          selectedEmiBank={selectedEmiBank}
          selectedEmiBankTitle={selectedEmiBankTitle}
          showCardForm={showCardForm}
        />
      ) : (
        <>
          {sortedEmiOptionsBasedOnEmiOffer?.map((item, idx) => {
            return (
              <EmiDisplayCard key={idx}
                data={item}
                divider={idx !== emiOptions.length - 1}
                onCardClick={handleSelectedBank}
              />
            );
          })}
        </>
      )}
    </div>
  );
}
